import { MouseEvent } from "react"
import { clsx } from "clsx"
import { useTranslation } from "react-i18next"

export type ColorSwatches =
	| "all"
	| "red"
	| "orange"
	| "yellow"
	| "green"
	| "aqua"
	| "blue"
	| "purple"
	| "magenta"
	| "black"
	| "gray"
	| "white"
	| "multigray"

const SWATCH_BG_CLASS: Record<ColorSwatches, string> = {
	all: "bg-[conic-gradient(#00FFFF,#001AFF,#FF00D6,#FF1F00,#FFC700,#00FF4C,#00FFFF)]",
	red: "bg-[hsl(3,100%,45%)]",
	orange: "bg-[hsl(32,94%,50%)]",
	yellow: "bg-[hsl(63,100%,45%)]",
	green: "bg-[hsl(153,100%,45%)]",
	aqua: "bg-[hsl(183,80%,50%)]",
	blue: "bg-[hsl(213,100%,50%)]",
	purple: "bg-[hsl(273,100%,61%)]",
	magenta: "bg-[hsl(303,100%,45%)]",
	black: "bg-[hsl(240,8%,5%)]",
	gray: "bg-[hsl(25,0%,65%)]",
	white: "bg-base-200 dark:bg-[hsl(240,100%,95%)]",
	multigray:
		"bg-[linear-gradient(90deg,rgba(0,0,0,1)_0%,rgba(0,0,0,1)_33%,rgba(70,70,70,1)_33%,rgba(70,70,70,1)_66%,rgba(175,175,175,1)_66%,rgba(218,218,218,1)_100%)]",
}

const SWATCH_BORDER_CLASS: Partial<Record<ColorSwatches, string>> = {
	red: "border-[hsl(3,100%,45%)]",
	orange: "border-[hsl(32,94%,50%)]",
	yellow: "border-[hsl(63,100%,45%)]",
	green: "border-[hsl(153,100%,45%)]",
	aqua: "border-[hsl(183,80%,50%)]",
	blue: "border-[hsl(213,100%,50%)]",
	purple: "border-[hsl(273,100%,61%)]",
	magenta: "border-[hsl(303,100%,45%)]",
	black: "border-[hsl(240,8%,5%)]",
	gray: "border-[hsl(25,0%,65%)]",
	white: "border-base-200 dark:border-[hsl(240,100%,95%)]",
}

/* Swatches component */

interface Props {
	selected?: ColorSwatches | null
	compact?: boolean
	colors: ColorSwatches[]
	onChange: (event: MouseEvent<HTMLDivElement>, item: ColorSwatches) => void
	className?: string
}
export const SwatchesList = (props: Props) => {
	const { t } = useTranslation("components")
	const renderSwatch = (color: ColorSwatches) => {
		const onChange = (event: MouseEvent<HTMLDivElement>) => {
			props.onChange(event, color)
		}

		const isSelected = props.selected === color
		const isGradient = color === "all" || color === "multigray"
		const bgClass = SWATCH_BG_CLASS[color]
		const borderClass = SWATCH_BORDER_CLASS[color]

		return (
			<div
				key={color}
				className={clsx(
					"box-border size-4 rounded-full border-solid",
					isGradient
						? isSelected
							? "border-2 border-base-50 dark:border-white"
							: "border border-gray-500"
						: "border-2",
					isGradient ? bgClass : borderClass,
					isGradient ? bgClass : isSelected ? bgClass : "bg-transparent",
				)}
				onClick={onChange}
				aria-label={t("swatches.swatch", { color: color })}
			/>
		)
	}

	return (
		<div
			className={clsx(
				"flex justify-between py-2",
				props.compact ? "gap-3" : "w-full",
				props.className,
			)}
		>
			{props.colors.map(renderSwatch)}
		</div>
	)
}
